/**
 * Home page – Facebook-style updates stream (recent matches + activity).
 * Win images, milestone badges, reactions, and comments on updates.
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useGame } from '../contexts/GameContext';
import { useLeaderboardCelebration } from '../contexts/LeaderboardCelebrationContext';
import { usePlayerCache } from '../contexts/PlayerCacheContext';
import * as readApi from '../api/readApi';
import { getDateKeyInTimezone, formatDateTimeInTimezone, toUtcMs, DEFAULT_TIMEZONE } from '../utils/dateTimeUtils';
import {
  generateLiveIntro,
  generateCompletedIntro,
  generateGenericIntro,
  generatePollCallout,
  generateSupportCallout,
} from '../utils/matchIntroGenerator';
import { LOG_TYPES } from '../services/matchUpdateLogService';
import MatchReactionStrip from '../components/MatchReactionStrip';
import FeedReactionStrip from '../components/FeedReactionStrip';
import FeedComments from '../components/FeedComments';
import GoogleAd from '../components/GoogleAd';
import LoadingBlock from '../components/LoadingBlock';
import MiniTournamentLeaderboard from '../components/MiniTournamentLeaderboard';
import './HomeFeed.css';

function formatScoreSummary(scores) {
  if (!scores) return '—';
  const games = [];
  if (scores.game1) games.push(`${scores.game1.player1}-${scores.game1.player2}`);
  if (scores.game2) games.push(`${scores.game2.player1}-${scores.game2.player2}`);
  if (scores.game3) games.push(`${scores.game3.player1}-${scores.game3.player2}`);
  return games.length ? games.join(', ') : '—';
}

function getMilestoneLabel(matchesWon) {
  if (matchesWon === 1) return 'First win!';
  if (matchesWon === 5) return '5th win!';
  if (matchesWon === 10) return '10th win!';
  if (matchesWon === 25) return '25th win!';
  if (matchesWon === 50) return '50th win!';
  if (matchesWon === 100) return '100th win!';
  return null;
}

function getFeedItemId(item) {
  if (item.kind === 'match_completed') return `match-${item.matchId}`;
  return `log-${item.id}`;
}

/** Normalize completedAt/performedAt to milliseconds for sort and display. */
function getItemTimestampMs(item) {
  const ts = item.completedAt ?? item.performedAt;
  return toUtcMs(ts);
}

/** Human-readable date label for section headers (Today, Yesterday, or formatted date). */
function getDateSectionLabel(dateKey, todayKey) {
  if (dateKey === todayKey) return 'Today';
  const today = new Date(todayKey);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayKey = yesterday.toISOString().slice(0, 10);
  if (dateKey === yesterdayKey) return 'Yesterday';
  const [y, m, d] = dateKey.split('-');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${parseInt(d, 10)} ${monthNames[parseInt(m, 10) - 1]} ${y}`;
}

/** For doubles: "Name1 & Name2" using player IDs and getPlayerName; fallback to teamLabel. */
function formatDoublesSide(playerIds, getPlayerName, teamLabel = 'Team') {
  if (!playerIds || playerIds.length === 0) return teamLabel;
  const names = playerIds.map((id) => getPlayerName(id)).filter(Boolean);
  return names.length > 0 ? names.join(' & ') : teamLabel;
}

export default function HomeFeed() {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const userTimezone = userProfile?.timezone || DEFAULT_TIMEZONE;
  const { gameId } = useGame();
  const { getPlayerName: getPlayerNameFromCache } = usePlayerCache();
  const celebration = useLeaderboardCelebration();
  const [feedItems, setFeedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tournamentNames, setTournamentNames] = useState({});
  const [matchDetails, setMatchDetails] = useState({});
  const [winnerMilestones, setWinnerMilestones] = useState({});
  const [playerNamesFromApi, setPlayerNamesFromApi] = useState({});
  const [feedReactionsMap, setFeedReactionsMap] = useState({});
  const [streamScrollTop, setStreamScrollTop] = useState(0);
  const streamRef = useRef(null);

  const handleStreamScroll = useCallback(() => {
    if (streamRef.current) setStreamScrollTop(streamRef.current.scrollTop);
  }, []);

  /** Prefer names from collated API response; fallback to player cache. */
  const getPlayerName = useMemo(() => (id) => playerNamesFromApi[id] || getPlayerNameFromCache(id) || 'Unknown', [playerNamesFromApi, getPlayerNameFromCache]);

  /** All updates in one feed, sorted by recency (most recent first). */
  const mergedFeed = useMemo(() => {
    const items = feedItems.map((item) => {
      const sortAtMs = getItemTimestampMs(item) ?? 0;
      return { ...item, sortAtMs };
    });
    items.sort((a, b) => b.sortAtMs - a.sortAtMs);
    return items.slice(0, 80);
  }, [feedItems]);

  const todayKey = getDateKeyInTimezone(Date.now(), userTimezone);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const data = await readApi.getFeedUpdates(gameId);
        if (cancelled) return;
        setFeedItems(data.items || []);
        setTournamentNames(data.tournamentNames || {});
        setMatchDetails(data.matchDetails || {});
        setWinnerMilestones(data.winnerMilestones || {});
        setPlayerNamesFromApi(data.playerNames || {});
        setFeedReactionsMap(data.feedReactionsMap || {});
      } catch (e) {
        console.error('[HomeFeed] load error', e);
        if (!cancelled) setFeedItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [gameId]);

  useEffect(() => {
    if (!loading && celebration?.checkAndShowCelebration) {
      celebration.checkAndShowCelebration();
    }
  }, [loading, celebration?.checkAndShowCelebration]);

  if (loading) {
    return (
      <div className="home-feed">
        <div className="home-feed-header">
          <h1 className="page-title">Updates</h1>
          <p className="home-feed-subtitle">Match started and completed</p>
        </div>
        <LoadingBlock message="Loading feed…" skeletonRows={5} className="home-feed-loading-block" />
      </div>
    );
  }

  return (
    <div className="home-feed">
      <div className="home-feed-header">
        <h1 className="page-title">Updates</h1>
        <p className="home-feed-subtitle">Match started and completed</p>
      </div>

      <GoogleAd slotKey="BANNER" className="google-ad-banner" />

      <MiniTournamentLeaderboard scrollTop={streamScrollTop} />

      <div
        ref={streamRef}
        className="home-feed-stream"
        onScroll={handleStreamScroll}
      >
        {mergedFeed.length === 0 ? (
          <div className="home-feed-empty card-ui">
            <p>No updates yet. Start or complete matches to see them here.</p>
            <button type="button" onClick={() => navigate('/schedule')} className="btn-primary">
              View schedule
            </button>
          </div>
        ) : (
          (() => {
            let lastDateKey = null;
            return mergedFeed.map((item) => {
              const tsMs = getItemTimestampMs(item);
              const dateKey = tsMs ? getDateKeyInTimezone(tsMs, userTimezone) : '';
              const showDateHeader = dateKey && dateKey !== lastDateKey;
              if (showDateHeader) lastDateKey = dateKey;

              return (
                <React.Fragment key={getFeedItemId(item)}>
                  {showDateHeader && (
                    <div className="home-feed-date-section">
                      {getDateSectionLabel(dateKey, todayKey)}
                    </div>
                  )}
                  {item.kind === 'match_completed' ? (() => {
              const md = matchDetails[item.matchId] || {};
              const team1Ids = md.team1Players || [];
              const p1 = item.matchType === 'doubles'
                ? formatDoublesSide(team1Ids, getPlayerName, 'Team 1')
                : (getPlayerName(item.player1Id) || 'Player 1');
              const p2 = item.matchType === 'doubles'
                ? formatDoublesSide(md.team2Players || [], getPlayerName, 'Team 2')
                : (getPlayerName(item.player2Id) || 'Player 2');
              const tournamentName = tournamentNames[item.tournamentId] || 'Tournament';
              const scoreText = formatScoreSummary(item.scores);
              const itemTsMs = getItemTimestampMs(item);
              const timeStr = itemTsMs ? formatDateTimeInTimezone(itemTsMs, userTimezone, { useSeparator: ' at ' }) : 'N/A';
              const winnerId = item.winnerId || (md.winnerIds && md.winnerIds[0]) || md.winnerId;
              const winnerIds = md.winnerIds || (winnerId ? [winnerId] : []);
              const team1Won = team1Ids.some((id) => winnerIds.includes(id) || id === winnerId);
              const winnerName = winnerId
                ? (item.matchType === 'doubles'
                  ? (team1Won ? p1 : p2)
                  : getPlayerName(winnerId))
                : null;
              const milestoneWins = item.winnerId ? winnerMilestones[item.winnerId] : null;
              const milestoneLabel = milestoneWins != null ? getMilestoneLabel(milestoneWins) : null;
              const matchDetail = matchDetails[item.matchId] || {};
              const reactionCounts1 = matchDetail.reactionCounts?.player1 || {};
              const reactionCounts2 = matchDetail.reactionCounts?.player2 || {};
              const feedItemId = getFeedItemId(item);
              const postReactions = feedReactionsMap[feedItemId] || {};

              return (
                <article key={`match-${item.matchId}`} className="home-feed-card home-feed-card-win card-ui">
                  <div className="home-feed-card-win-visual" aria-hidden>
                    <span className="home-feed-card-win-image">🏆</span>
                    <span className="home-feed-card-win-confetti" aria-hidden />
                  </div>
                  <div className="home-feed-card-header">
                    <span className="home-feed-card-avatar home-feed-card-avatar-win" aria-hidden>🏓</span>
                    <div className="home-feed-card-meta">
                      <span className="home-feed-card-title home-feed-card-title-critical">Match completed</span>
                      {milestoneLabel && (
                        <span className="home-feed-milestone-badge">{milestoneLabel}</span>
                      )}
                      <span className="home-feed-card-by">
                        {matchDetails[item.matchId]?.refereeName && `Ref: ${matchDetails[item.matchId].refereeName} · `}
                        {timeStr}
                      </span>
                    </div>
                  </div>
                  <div className="home-feed-card-body">
                    <p className="home-feed-card-text home-feed-card-text-critical">
                      {winnerName ? (
                        <span className="home-feed-card-winner">
                          {generateCompletedIntro(winnerName, p1, p2, scoreText, item.matchId)}
                        </span>
                      ) : (
                        <>
                          Match complete: <strong>{p1}</strong> vs <strong>{p2}</strong>
                          {scoreText !== '—' && <> · {scoreText}</>}
                        </>
                      )}
                    </p>
                    <p className="home-feed-card-context">{tournamentName}</p>
                    {currentUser && (
                      <p className="home-feed-card-poll-callout">
                        {generateSupportCallout(`${item.matchId}-support`)}
                      </p>
                    )}
                  </div>
                  {currentUser && (
                    <div className="home-feed-card-reactions">
                      <MatchReactionStrip
                        matchId={item.matchId}
                        team="player1"
                        teamName={p1}
                        reactionCounts={reactionCounts1}
                        onReactionUpdate={(mid, team, emojiIndex, delta) => {
                          setMatchDetails((prev) => ({
                            ...prev,
                            [mid]: {
                              ...prev[mid],
                              reactionCounts: {
                                ...prev[mid]?.reactionCounts,
                                [team]: {
                                  ...(prev[mid]?.reactionCounts?.[team] || {}),
                                  [emojiIndex]: Math.max(0, ((prev[mid]?.reactionCounts?.[team]?.[emojiIndex]) || 0) + delta),
                                },
                              },
                            },
                          }));
                        }}
                      />
                      <MatchReactionStrip
                        matchId={item.matchId}
                        team="player2"
                        teamName={p2}
                        reactionCounts={reactionCounts2}
                        onReactionUpdate={(mid, team, emojiIndex, delta) => {
                          setMatchDetails((prev) => ({
                            ...prev,
                            [mid]: {
                              ...prev[mid],
                              reactionCounts: {
                                ...prev[mid]?.reactionCounts,
                                [team]: {
                                  ...(prev[mid]?.reactionCounts?.[team] || {}),
                                  [emojiIndex]: Math.max(0, ((prev[mid]?.reactionCounts?.[team]?.[emojiIndex]) || 0) + delta),
                                },
                              },
                            },
                          }));
                        }}
                      />
                    </div>
                  )}
                  <FeedReactionStrip
                    feedItemId={feedItemId}
                    reactionCounts={postReactions}
                    onReactionUpdate={(fid, emojiIndex, delta) => {
                      setFeedReactionsMap((prev) => ({
                        ...prev,
                        [fid]: {
                          ...(prev[fid] || {}),
                          [emojiIndex]: Math.max(0, ((prev[fid]?.[emojiIndex]) || 0) + delta),
                        },
                      }));
                    }}
                  />
                  <FeedComments feedItemId={feedItemId} />
                  <div className="home-feed-card-actions">
                    <button
                      type="button"
                      onClick={() => navigate(`/match/${item.matchId}`)}
                      className="btn-secondary home-feed-card-btn"
                    >
                      View match
                    </button>
                  </div>
                </article>
              );
            })() : item.kind === 'log' && item.type === LOG_TYPES.STARTED ? (() => {
              const m = matchDetails[item.matchId];
              const p1 = m?.matchType === 'doubles'
                ? formatDoublesSide(m?.team1Players, getPlayerName, 'Team 1')
                : (getPlayerName(m?.player1Id) || 'Player 1');
              const p2 = m?.matchType === 'doubles'
                ? formatDoublesSide(m?.team2Players, getPlayerName, 'Team 2')
                : (getPlayerName(m?.player2Id) || 'Player 2');
              const tournamentName = tournamentNames[item.tournamentId || m?.tournamentId] || '';
              const tsMs = getItemTimestampMs(item);
              const timeStr = tsMs ? formatDateTimeInTimezone(tsMs, userTimezone, { useSeparator: ' at ' }) : 'N/A';
              const feedItemIdLog = getFeedItemId(item);
              const postReactionsLog = feedReactionsMap[feedItemIdLog] || {};
              const liveIntro = generateLiveIntro(p1, p2, feedItemIdLog);
              const pollCallout = generatePollCallout(`${feedItemIdLog}-poll`);
              const matchDetail = matchDetails[item.matchId] || {};
              const reactionCounts1 = matchDetail.reactionCounts?.player1 || {};
              const reactionCounts2 = matchDetail.reactionCounts?.player2 || {};

              return (
                <article key={`log-${item.id}`} className="home-feed-card home-feed-card-live card-ui">
                  <div className="home-feed-card-live-visual" aria-hidden>
                    <span className="home-feed-card-live-dot" aria-hidden />
                    <span className="home-feed-card-live-image">▶</span>
                    <span className="home-feed-card-live-label">LIVE</span>
                  </div>
                  <div className="home-feed-card-header">
                    <span className="home-feed-card-avatar home-feed-card-avatar-live" aria-hidden>🏓</span>
                    <div className="home-feed-card-meta">
                      <span className="home-feed-card-title home-feed-card-title-critical">Match started</span>
                      <span className="home-feed-card-by">
                        {item.performedByName && `Started by ${item.performedByName} · `}
                        {timeStr}
                      </span>
                    </div>
                  </div>
                  <div className="home-feed-card-body">
                    <p className="home-feed-card-text home-feed-card-text-critical">
                      {liveIntro}
                    </p>
                    {tournamentName && (
                      <p className="home-feed-card-context">{tournamentName}</p>
                    )}
                    {currentUser && item.matchId && (
                      <>
                        <p className="home-feed-card-poll-callout">{pollCallout}</p>
                        <div className="home-feed-card-reactions">
                          <MatchReactionStrip
                            matchId={item.matchId}
                            team="player1"
                            teamName={p1}
                            reactionCounts={reactionCounts1}
                            onReactionUpdate={(mid, team, emojiIndex, delta) => {
                              setMatchDetails((prev) => ({
                                ...prev,
                                [mid]: {
                                  ...prev[mid],
                                  reactionCounts: {
                                    ...prev[mid]?.reactionCounts,
                                    [team]: {
                                      ...(prev[mid]?.reactionCounts?.[team] || {}),
                                      [emojiIndex]: Math.max(0, ((prev[mid]?.reactionCounts?.[team]?.[emojiIndex]) || 0) + delta),
                                    },
                                  },
                                },
                              }));
                            }}
                          />
                          <MatchReactionStrip
                            matchId={item.matchId}
                            team="player2"
                            teamName={p2}
                            reactionCounts={reactionCounts2}
                            onReactionUpdate={(mid, team, emojiIndex, delta) => {
                              setMatchDetails((prev) => ({
                                ...prev,
                                [mid]: {
                                  ...prev[mid],
                                  reactionCounts: {
                                    ...prev[mid]?.reactionCounts,
                                    [team]: {
                                      ...(prev[mid]?.reactionCounts?.[team] || {}),
                                      [emojiIndex]: Math.max(0, ((prev[mid]?.reactionCounts?.[team]?.[emojiIndex]) || 0) + delta),
                                    },
                                  },
                                },
                              }));
                            }}
                          />
                        </div>
                      </>
                    )}
                  </div>
                  <FeedReactionStrip
                    feedItemId={feedItemIdLog}
                    reactionCounts={postReactionsLog}
                    onReactionUpdate={(fid, emojiIndex, delta) => {
                      setFeedReactionsMap((prev) => ({
                        ...prev,
                        [fid]: {
                          ...(prev[fid] || {}),
                          [emojiIndex]: Math.max(0, ((prev[fid]?.[emojiIndex]) || 0) + delta),
                        },
                      }));
                    }}
                  />
                  <FeedComments feedItemId={feedItemIdLog} />
                  {item.matchId && (
                    <div className="home-feed-card-actions">
                      <button
                        type="button"
                        onClick={() => navigate(`/match/${item.matchId}`)}
                        className="btn-primary home-feed-card-btn"
                      >
                        Score match
                      </button>
                    </div>
                  )}
                </article>
              );
            })() : item.kind === 'log' ? (() => {
              const m = matchDetails[item.matchId];
              const p1 = m?.matchType === 'doubles'
                ? formatDoublesSide(m?.team1Players, getPlayerName, 'Team 1')
                : (getPlayerName(m?.player1Id) || 'Player 1');
              const p2 = m?.matchType === 'doubles'
                ? formatDoublesSide(m?.team2Players, getPlayerName, 'Team 2')
                : (getPlayerName(m?.player2Id) || 'Player 2');
              const tournamentName = tournamentNames[item.tournamentId || m?.tournamentId] || '';
              const tsMs = getItemTimestampMs(item);
              const timeStr = tsMs ? formatDateTimeInTimezone(tsMs, userTimezone, { useSeparator: ' at ' }) : 'N/A';
              const feedItemIdLog = getFeedItemId(item);
              const postReactionsLog = feedReactionsMap[feedItemIdLog] || {};
              const typeLabels = {
                [LOG_TYPES.CREATED]: 'Match created',
                [LOG_TYPES.SCHEDULED]: 'Match scheduled',
                [LOG_TYPES.RESCHEDULE_REQUESTED]: 'Reschedule requested',
                [LOG_TYPES.RESCHEDULE_APPROVED]: 'Reschedule approved',
                [LOG_TYPES.RESCHEDULE_COMPLETED]: 'Reschedule completed',
                [LOG_TYPES.REFEREE_CHANGED]: 'Referee changed',
                [LOG_TYPES.COMPLETED]: 'Match completed',
              };
              const title = typeLabels[item.type] || item.type || 'Update';
              const genericIntro = p1 && p2 ? generateGenericIntro(p1, p2, feedItemIdLog) : null;
              const showPoll = (item.type === LOG_TYPES.CREATED || item.type === LOG_TYPES.SCHEDULED) && p1 && p2 && item.matchId && currentUser;
              const pollCallout = showPoll ? generatePollCallout(`${feedItemIdLog}-poll`) : null;
              const matchDetail = matchDetails[item.matchId] || {};
              const reactionCounts1 = matchDetail.reactionCounts?.player1 || {};
              const reactionCounts2 = matchDetail.reactionCounts?.player2 || {};

              return (
                <article key={`log-${item.id}`} className="home-feed-card card-ui">
                  <div className="home-feed-card-header">
                    <span className="home-feed-card-avatar" aria-hidden>🏓</span>
                    <div className="home-feed-card-meta">
                      <span className="home-feed-card-title">{title}</span>
                      <span className="home-feed-card-by">
                        {item.performedByName && `${item.performedByName} · `}
                        {timeStr}
                      </span>
                    </div>
                  </div>
                  <div className="home-feed-card-body">
                    {genericIntro && (
                      <p className="home-feed-card-text">
                        {genericIntro}
                      </p>
                    )}
                    {showPoll && (
                      <>
                        <p className="home-feed-card-poll-callout">{pollCallout}</p>
                        <div className="home-feed-card-reactions">
                          <MatchReactionStrip
                            matchId={item.matchId}
                            team="player1"
                            teamName={p1}
                            reactionCounts={reactionCounts1}
                            onReactionUpdate={(mid, team, emojiIndex, delta) => {
                              setMatchDetails((prev) => ({
                                ...prev,
                                [mid]: {
                                  ...prev[mid],
                                  reactionCounts: {
                                    ...prev[mid]?.reactionCounts,
                                    [team]: {
                                      ...(prev[mid]?.reactionCounts?.[team] || {}),
                                      [emojiIndex]: Math.max(0, ((prev[mid]?.reactionCounts?.[team]?.[emojiIndex]) || 0) + delta),
                                    },
                                  },
                                },
                              }));
                            }}
                          />
                          <MatchReactionStrip
                            matchId={item.matchId}
                            team="player2"
                            teamName={p2}
                            reactionCounts={reactionCounts2}
                            onReactionUpdate={(mid, team, emojiIndex, delta) => {
                              setMatchDetails((prev) => ({
                                ...prev,
                                [mid]: {
                                  ...prev[mid],
                                  reactionCounts: {
                                    ...prev[mid]?.reactionCounts,
                                    [team]: {
                                      ...(prev[mid]?.reactionCounts?.[team] || {}),
                                      [emojiIndex]: Math.max(0, ((prev[mid]?.reactionCounts?.[team]?.[emojiIndex]) || 0) + delta),
                                    },
                                  },
                                },
                              }));
                            }}
                          />
                        </div>
                      </>
                    )}
                    {tournamentName && (
                      <p className="home-feed-card-context">{tournamentName}</p>
                    )}
                  </div>
                  <FeedReactionStrip
                    feedItemId={feedItemIdLog}
                    reactionCounts={postReactionsLog}
                    onReactionUpdate={(fid, emojiIndex, delta) => {
                      setFeedReactionsMap((prev) => ({
                        ...prev,
                        [fid]: {
                          ...(prev[fid] || {}),
                          [emojiIndex]: Math.max(0, ((prev[fid]?.[emojiIndex]) || 0) + delta),
                        },
                      }));
                    }}
                  />
                  <FeedComments feedItemId={feedItemIdLog} />
                  {item.matchId && (
                    <div className="home-feed-card-actions">
                      <button
                        type="button"
                        onClick={() => navigate(`/match/${item.matchId}`)}
                        className="btn-secondary home-feed-card-btn"
                      >
                        View match
                      </button>
                    </div>
                  )}
                </article>
              );
            })() : null}
                </React.Fragment>
              );
            });
          })()
        )}
      </div>
    </div>
  );
}
